import { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { MainLayout } from '../components/layout/MainLayout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import { ScrollArea } from '../components/ui/scroll-area';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../components/ui/accordion';
import { toast } from 'sonner';
import { useContentProtection } from '../hooks/useContentProtection';
import { 
    Monitor,
    Server,
    Code,
    Globe,
    Smartphone,
    Cloud,
    GitBranch,
    Database,
    Brain,
    HardDrive,
    CheckCircle,
    AlertCircle,
    Shield,
    Building,
    Network,
    Sparkles,
    Users,
    Search,
    Play,
    Lock,
    ChevronRight,
    BookOpen,
    GraduationCap,
    Target,
    Award,
    Loader2,
    Filter,
    Zap,
    TrendingUp,
    Clock,
    Star,
    Wrench,
    Cog,
    Plane,
    Car,
    Factory,
    Leaf,
    Heart,
    Beaker,
    Pill,
    Stethoscope,
    TestTube,
    FileText,
    ClipboardList,
    Microscope,
    Activity,
    Truck,
    Settings,
    Package,
    DollarSign
} from 'lucide-react';

// Icon mapping for categories
const iconMap = {
    Monitor: Monitor,
    Server: Server,
    Code: Code,
    Globe: Globe,
    Smartphone: Smartphone,
    Cloud: Cloud,
    GitBranch: GitBranch,
    Database: Database,
    Brain: Brain,
    HardDrive: HardDrive,
    CheckCircle: CheckCircle,
    Shield: Shield,
    Building: Building,
    Network: Network,
    Sparkles: Sparkles,
    Users: Users,
    Folder: BookOpen,
    Terminal: Code,
    Layers: Database,
    Coffee: Code,
    Cpu: Server,
    Hash: Code,
    FileCode: Code,
    Zap: Zap,
    Settings: Settings,
    Gem: Code,
    Flame: Code,
    Layout: Globe,
    Palette: Globe,
    Ship: Cloud,
    RefreshCw: GitBranch,
    BarChart: Database,
    PieChart: Database,
    ArrowLeftRight: Database,
    GitMerge: Database,
    TrendingUp: TrendingUp,
    MessageSquare: Brain,
    Eye: Brain,
    Workflow: Brain,
    Lightbulb: Brain,
    Archive: Database,
    ClipboardCheck: CheckCircle,
    Bot: CheckCircle,
    Gauge: CheckCircle,
    Lock: Shield,
    Key: Shield,
    FileCheck: Shield,
    Cog: Cog,
    Headphones: Network,
    Link: Sparkles,
    Wifi: Sparkles,
    Glasses: Sparkles,
    Atom: Sparkles,
    Clipboard: Users,
    Building2: Users,
    ShieldCheck: Shield,
    Clouds: Cloud,
    // Engineering vertical icons
    Wrench: Wrench,
    Plane: Plane,
    Car: Car,
    Factory: Factory,
    Leaf: Leaf,
    Heart: Heart,
    Truck: Truck,
    Package: Package,
    // Pharma & Healthcare vertical icons
    Pill: Pill,
    Beaker: Beaker,
    Stethoscope: Stethoscope,
    TestTube: TestTube,
    FileText: FileText,
    ClipboardList: ClipboardList,
    Microscope: Microscope,
    Activity: Activity,
    DollarSign: DollarSign
};

const difficultyColors = {
    beginner: 'bg-emerald-500/10 text-emerald-600 border-emerald-500/30',
    intermediate: 'bg-amber-500/10 text-amber-600 border-amber-500/30',
    advanced: 'bg-orange-500/10 text-orange-600 border-orange-500/30',
    expert: 'bg-red-500/10 text-red-600 border-red-500/30'
};

const statusColors = {
    not_started: 'bg-muted text-muted-foreground',
    in_progress: 'bg-blue-500/10 text-blue-600',
    completed: 'bg-emerald-500/10 text-emerald-600'
};

const JobSimulations = () => {
    const { api, user: currentUser } = useAuth();
    
    // Enable content protection (disable right-click, copy, screenshots)
    useContentProtection(true);
    
    const [verticals, setVerticals] = useState([]);
    const [selectedVertical, setSelectedVertical] = useState(null);
    const [categories, setCategories] = useState([]);
    const [selectedCategory, setSelectedCategory] = useState(null);
    const [categorySkills, setCategorySkills] = useState([]);
    const [loadingSkills, setLoadingSkills] = useState(false);
    const [skills, setSkills] = useState([]);
    const [selectedSkill, setSelectedSkill] = useState(null);
    const [loading, setLoading] = useState(true);
    const [searchQuery, setSearchQuery] = useState('');
    const [userProgress, setUserProgress] = useState(null);
    const [initializing, setInitializing] = useState(false);

    useEffect(() => {
        fetchVerticals();
        fetchUserProgress();
    }, []);

    useEffect(() => {
        if (selectedVertical) {
            // Reset selected category and skills when vertical changes
            setSelectedCategory(null);
            setCategorySkills([]);
            fetchVerticalDetails(selectedVertical.id);
        }
    }, [selectedVertical]);

    const fetchVerticals = async () => {
        try {
            const response = await api.get('/job-simulations/verticals');
            setVerticals(response.data);
            if (response.data.length > 0) {
                setSelectedVertical(response.data[0]);
            }
        } catch (error) {
            // If no data, might need initialization
            if (currentUser?.role === 'admin') {
                setVerticals([]);
            }
        } finally {
            setLoading(false);
        }
    };

    const fetchVerticalDetails = async (verticalId) => {
        try {
            const response = await api.get(`/job-simulations/verticals/${verticalId}`);
            setCategories(response.data.categories || []);
        } catch (error) {
            console.error('Error fetching vertical details:', error);
        }
    };

    const fetchUserProgress = async () => {
        try {
            const response = await api.get('/job-simulations/progress');
            setUserProgress(response.data);
        } catch (error) {
            console.error('Error fetching progress:', error);
        }
    };

    const fetchCategorySkills = async (categoryId) => {
        try {
            const response = await api.get(`/job-simulations/categories/${categoryId}`);
            return response.data.skills || [];
        } catch (error) {
            console.error('Error fetching skills:', error);
            return [];
        }
    };

    const handleCategorySelect = async (category) => {
        setSelectedCategory(category);
        setLoadingSkills(true);
        try {
            const skills = await fetchCategorySkills(category.id);
            setCategorySkills(skills);
        } catch (error) {
            console.error('Error loading skills:', error);
            setCategorySkills([]);
        } finally {
            setLoadingSkills(false);
        }
    };

    // Auto-select first category when categories load
    useEffect(() => {
        if (categories.length > 0 && !selectedCategory) {
            handleCategorySelect(categories[0]);
        }
    }, [categories]);

    const initializeJobSimulations = async () => {
        setInitializing(true);
        try {
            const response = await api.post('/job-simulations/initialize');
            toast.success(`Initialized ${response.data.skills} skills across ${response.data.categories} categories`);
            await fetchVerticals();
        } catch (error) {
            toast.error('Failed to initialize');
        } finally {
            setInitializing(false);
        }
    };

    const getIcon = (iconName) => {
        const IconComponent = iconMap[iconName] || Code;
        return IconComponent;
    };

    const getProgressForSkill = (skillId) => {
        if (!userProgress?.skills) return null;
        return userProgress.skills.find(p => p.skill_id === skillId);
    };

    const filteredCategories = categories.filter(cat => 
        !searchQuery || 
        cat.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        cat.description.toLowerCase().includes(searchQuery.toLowerCase())
    );

    const isAdmin = currentUser?.role === 'admin' || currentUser?.role === 'trainer';

    if (loading) {
        return (
            <MainLayout>
                <div className="flex items-center justify-center h-[60vh]">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary" />
                </div>
            </MainLayout>
        );
    }

    // Show initialization screen if no verticals
    if (verticals.length === 0) {
        return (
            <MainLayout>
                <div className="flex flex-col items-center justify-center h-[60vh] space-y-6" data-testid="job-simulations-init">
                    <div className="p-6 rounded-full bg-primary/10">
                        <GraduationCap className="h-16 w-16 text-primary" />
                    </div>
                    <div className="text-center space-y-2">
                        <h2 className="text-2xl font-bold">Job Simulations</h2>
                        <p className="text-muted-foreground max-w-md">
                            Comprehensive skill training system for recruiters. Learn technologies, 
                            practice job analysis, and master candidate screening.
                        </p>
                    </div>
                    {isAdmin ? (
                        <Button 
                            onClick={initializeJobSimulations} 
                            disabled={initializing}
                            size="lg"
                            className="gap-2"
                            data-testid="initialize-btn"
                        >
                            {initializing ? (
                                <Loader2 className="h-4 w-4 animate-spin" />
                            ) : (
                                <Zap className="h-4 w-4" />
                            )}
                            {initializing ? 'Initializing...' : 'Initialize IT Skills Library'}
                        </Button>
                    ) : (
                        <p className="text-sm text-muted-foreground">
                            Please ask an administrator to initialize the skills library.
                        </p>
                    )}
                </div>
            </MainLayout>
        );
    }

    return (
        <MainLayout>
            <div className="space-y-6 animate-fade-in" data-testid="job-simulations-page">
                {/* Header */}
                <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                    <div>
                        <h1 className="text-3xl font-heading font-bold tracking-tight flex items-center gap-3">
                            <GraduationCap className="h-8 w-8 text-primary" />
                            Job Simulations
                        </h1>
                        <p className="text-muted-foreground mt-1">
                            Master recruiting skills through structured learning and simulations
                        </p>
                    </div>
                    <div className="flex items-center gap-3">
                        {/* Progress Summary */}
                        {userProgress && (
                            <div className="flex items-center gap-4 px-4 py-2 rounded-xl bg-muted/50">
                                <div className="text-center">
                                    <p className="text-2xl font-bold text-primary">{userProgress.summary.completed_skills}</p>
                                    <p className="text-xs text-muted-foreground">Completed</p>
                                </div>
                                <div className="w-px h-8 bg-border" />
                                <div className="text-center">
                                    <p className="text-2xl font-bold text-blue-500">{userProgress.summary.in_progress_skills}</p>
                                    <p className="text-xs text-muted-foreground">In Progress</p>
                                </div>
                                <div className="w-px h-8 bg-border" />
                                <div className="text-center">
                                    <p className="text-2xl font-bold">{userProgress.summary.total_simulations}</p>
                                    <p className="text-xs text-muted-foreground">Simulations</p>
                                </div>
                            </div>
                        )}
                    </div>
                </div>

                {/* Vertical Tabs */}
                {verticals.length > 1 && (
                    <div className="flex flex-wrap gap-2" data-testid="vertical-tabs">
                        {verticals.map((vertical) => {
                            const Icon = getIcon(vertical.icon);
                            const isSelected = selectedVertical?.id === vertical.id;
                            return (
                                <Button
                                    key={vertical.id}
                                    variant={isSelected ? "default" : "outline"}
                                    className={`gap-2 ${isSelected ? '' : 'bg-card hover:bg-muted'}`}
                                    onClick={() => setSelectedVertical(vertical)}
                                    data-testid={`vertical-tab-${vertical.id}`}
                                >
                                    <Icon className="h-4 w-4" />
                                    {vertical.name}
                                    <Badge variant="secondary" className="ml-1">
                                        {vertical.category_count}
                                    </Badge>
                                </Button>
                            );
                        })}
                    </div>
                )}

                {/* Search Bar */}
                <div className="relative max-w-md">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                    <Input
                        placeholder="Search skills, technologies..."
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        className="pl-10"
                        data-testid="skill-search"
                    />
                </div>

                {/* Main Content */}
                <div className="grid gap-6 lg:grid-cols-4">
                    {/* Categories Sidebar */}
                    <div className="lg:col-span-1">
                        <Card>
                            <CardHeader className="pb-3">
                                <CardTitle className="text-lg flex items-center gap-2">
                                    {(() => {
                                        const VerticalIcon = getIcon(selectedVertical?.icon || 'Monitor');
                                        return <VerticalIcon className="h-5 w-5 text-primary" />;
                                    })()}
                                    {selectedVertical?.name || 'Skills'}
                                </CardTitle>
                                <CardDescription>
                                    {categories.length} categories • {categories.reduce((sum, cat) => sum + (cat.skill_count || 0), 0)} skills
                                </CardDescription>
                            </CardHeader>
                            <CardContent className="p-0">
                                <ScrollArea className="h-[calc(100vh-350px)] min-h-[400px]">
                                    <div className="p-2 space-y-1">
                                        {filteredCategories.map((category) => {
                                            const Icon = getIcon(category.icon);
                                            const isSelected = selectedCategory?.id === category.id;
                                            return (
                                                <button
                                                    key={category.id}
                                                    onClick={() => handleCategorySelect(category)}
                                                    className={`w-full flex items-center gap-3 p-3 rounded-lg transition-colors text-left ${
                                                        isSelected 
                                                            ? 'bg-primary/10 border border-primary/30' 
                                                            : 'hover:bg-muted/50'
                                                    }`}
                                                    data-testid={`category-${category.id}`}
                                                >
                                                    <div className={`p-2 rounded-lg ${isSelected ? 'bg-primary/20' : 'bg-primary/10'}`}>
                                                        <Icon className={`h-4 w-4 ${isSelected ? 'text-primary' : 'text-primary/70'}`} />
                                                    </div>
                                                    <div className="flex-1 min-w-0">
                                                        <p className={`font-medium text-sm truncate ${isSelected ? 'text-primary' : ''}`}>{category.name}</p>
                                                        <p className="text-xs text-muted-foreground">{category.skill_count} skills</p>
                                                    </div>
                                                    <ChevronRight className={`h-4 w-4 ${isSelected ? 'text-primary' : 'text-muted-foreground'}`} />
                                                </button>
                                            );
                                        })}
                                    </div>
                                </ScrollArea>
                            </CardContent>
                        </Card>
                    </div>

                    {/* Skills Panel - Shows skills for selected category */}
                    <div className="lg:col-span-3">
                        <Card>
                            <CardHeader>
                                <div className="flex items-center justify-between">
                                    <div>
                                        <CardTitle className="flex items-center gap-2">
                                            {selectedCategory && (() => {
                                                const CatIcon = getIcon(selectedCategory.icon);
                                                return <CatIcon className="h-5 w-5 text-primary" />;
                                            })()}
                                            {selectedCategory?.name || 'Select a Category'}
                                        </CardTitle>
                                        <CardDescription>
                                            {selectedCategory 
                                                ? `${categorySkills.length} skills available • Click to start learning`
                                                : 'Choose a category from the left to view skills'
                                            }
                                        </CardDescription>
                                    </div>
                                    {selectedCategory && (
                                        <Badge variant="outline" className="text-xs">
                                            {selectedCategory.skill_count} skills
                                        </Badge>
                                    )}
                                </div>
                            </CardHeader>
                            <CardContent>
                                {loadingSkills ? (
                                    <div className="flex items-center justify-center py-12">
                                        <Loader2 className="h-8 w-8 animate-spin text-primary" />
                                    </div>
                                ) : !selectedCategory ? (
                                    <div className="text-center py-12 text-muted-foreground">
                                        <BookOpen className="h-12 w-12 mx-auto mb-4 opacity-30" />
                                        <p>Select a category from the left panel to view skills</p>
                                    </div>
                                ) : categorySkills.length === 0 ? (
                                    <div className="text-center py-12 text-muted-foreground">
                                        <AlertCircle className="h-12 w-12 mx-auto mb-4 opacity-30" />
                                        <p>No skills found in this category</p>
                                    </div>
                                ) : (
                                    <div className="grid gap-3 md:grid-cols-2">
                                        {categorySkills.map((skill) => {
                                            const SkillIcon = getIcon(skill.icon);
                                            const progress = getProgressForSkill(skill.id);
                                            return (
                                                <SkillCard
                                                    key={skill.id}
                                                    skill={skill}
                                                    SkillIcon={SkillIcon}
                                                    progress={progress}
                                                    difficultyColors={difficultyColors}
                                                    statusColors={statusColors}
                                                />
                                            );
                                        })}
                                    </div>
                                )}
                            </CardContent>
                        </Card>
                    </div>
                </div>
            </div>
        </MainLayout>
    );
};

// Skill Card Component
const SkillCard = ({ skill, SkillIcon, progress, difficultyColors, statusColors }) => {
    const navigate = useNavigate();
    
    const getStatusInfo = () => {
        if (!progress) return { status: 'not_started', label: 'Start Learning' };
        if (progress.completed) return { status: 'completed', label: 'Completed' };
        if (progress.lessons_completed > 0) return { status: 'in_progress', label: `${progress.lessons_completed}/${progress.total_lessons || 5} Lessons` };
        return { status: 'not_started', label: 'Start Learning' };
    };
    
    const { status, label } = getStatusInfo();
    
    return (
        <div
            onClick={() => navigate(`/skill-learning/${skill.id}`)}
            className="p-4 rounded-xl border hover:border-primary/50 hover:bg-primary/5 transition-all cursor-pointer group"
            data-testid={`skill-${skill.id}`}
        >
            <div className="flex items-start gap-3">
                <div className="p-2 rounded-lg bg-primary/10 group-hover:bg-primary/20 transition-colors">
                    <SkillIcon className="h-5 w-5 text-primary" />
                </div>
                <div className="flex-1 min-w-0">
                    <h4 className="font-medium text-sm group-hover:text-primary transition-colors truncate">
                        {skill.name}
                    </h4>
                    <p className="text-xs text-muted-foreground line-clamp-2 mt-1">
                        {skill.description}
                    </p>
                    <div className="flex items-center gap-2 mt-2">
                        <Badge 
                            variant="outline" 
                            className={`text-xs ${difficultyColors[skill.difficulty_base] || difficultyColors.beginner}`}
                        >
                            {skill.difficulty_base}
                        </Badge>
                        <Badge 
                            variant="outline" 
                            className={`text-xs ${statusColors[status]}`}
                        >
                            {label}
                        </Badge>
                    </div>
                </div>
                <ChevronRight className="h-4 w-4 text-muted-foreground group-hover:text-primary transition-colors" />
            </div>
        </div>
    );
};

export default JobSimulations;
